import { ElementType, ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useMediaQuery } from '@mui/material';

import { i18nNS } from '../../i18n';
import { randomStr } from '../../utils/helper';
import { HeaderActionProps, Props as HeaderProps } from '../Header';

type Dismissal = 'esc' | 'backdrop';
export interface Props<L extends ElementType<any> = 'button', R extends ElementType<any> = 'button'> {
  /**
   * Drawer dismissal behaviour
   * @default ['esc', 'backdrop']
   */
  dismissal?: 'none' | Dismissal[];
  header?:
    | string
    | ((id: string) => ReactNode)
    | {
        leftButtonProps?: HeaderActionProps<L>;
        rightButtonProps?: HeaderActionProps<R>;
        title: string;
      };
  onClose: () => void;
}

const useDrawerVM = <L extends ElementType<any> = 'button', R extends ElementType<any> = 'button'>({
  dismissal = ['esc', 'backdrop'],
  header,
  onClose,
}: Props<L, R>) => {
  const { t } = useTranslation([i18nNS.COMMON]);

  const { titleId, bodyId } = useMemo(() => ({ titleId: randomStr(6), bodyId: randomStr(6) }), []);

  const handleDismiss = useCallback(
    (_, reason: 'backdropClick' | 'escapeKeyDown') => {
      if (dismissal === 'none') return;

      if (reason === 'backdropClick' && dismissal.includes('backdrop')) {
        return onClose();
      }

      if (reason === 'escapeKeyDown' && dismissal.includes('esc')) {
        return onClose();
      }
    },
    [dismissal, onClose]
  );

  const headerProps = useMemo<HeaderProps | undefined>(() => {
    if (!header || typeof header === 'function') return undefined;

    const dismissButtonProps: HeaderActionProps = {
      'aria-label': t('click_to_close_drawer', { ns: i18nNS.COMMON }),
      children: undefined,
      onClick: onClose,
    };

    if (typeof header === 'string') {
      return {
        id: titleId,
        children: header,
        left: undefined,
        right: undefined,
      };
    }

    return {
      id: titleId,
      children: header.title,
      left: header.leftButtonProps,
      right: header.rightButtonProps,
    };
  }, [header, onClose, t, titleId]);

  return { bodyId, handleDismiss, headerProps, titleId };
};

export default useDrawerVM;
